import type { QueryClient } from "@tanstack/react-query";
import type { NavigateFunction } from "react-router-dom";
import { enqueueSnackbar } from "notistack";
import { getAndClearRedirectUrl } from "@/shared/utils/redirectHandlers";
import type { User, UserLoginType, APIResponseType } from "@/shared/types";
import { setAuthTokens } from "@/shared/auth/authTokens";
import { getPostAuthPath } from "@/shared/auth/postAuthPath";

/** Same default as email/password sign-in (`SignInForm`). */
const DEFAULT_POST_LOGIN_PATH = "/TNPage";

/** Used when OAuth returns on guest routes (e.g. Kakao) where `navigate` cannot pass `state`. */
export const POST_ADD_USERNAME_REDIRECT_KEY = "postAddUsernameRedirect";

export type ApplySocialLoginSessionParams = {
  data: APIResponseType<UserLoginType>;
  queryClient: QueryClient;
  setUser: (user: User | null) => void;
  restoreSession: (user: User) => void;
  navigate: NavigateFunction;
  redirectPath?: string;
};

/** Shared post-SSO session + redirect logic (Google, Kakao, etc.). */
export function applySocialLoginSession({
  data,
  queryClient,
  setUser,
  restoreSession,
  navigate: _navigate,
  redirectPath,
}: ApplySocialLoginSessionParams): void {
  if (data.data?.access_token) {
    setAuthTokens(data.data.access_token, data.data.refresh_token);
  }

  if (data.data?.user) {
    restoreSession(data.data.user);
    setUser(data.data.user);
    queryClient.setQueryData<APIResponseType<User>>(["user-profile"], {
      message: data.message ?? "",
      data: data.data.user,
    });
  }

  const storedRedirect = getAndClearRedirectUrl();

  if (storedRedirect) {
    enqueueSnackbar("Login successful.", { variant: "success" });
    setTimeout(() => {
      window.location.href = storedRedirect;
    }, 200);
    return;
  }

  const destination = getPostAuthPath(data.data?.user, redirectPath ?? DEFAULT_POST_LOGIN_PATH);

  if (data.data?.user && !data.data.user.username) {
    sessionStorage.setItem(POST_ADD_USERNAME_REDIRECT_KEY, destination);
    window.location.assign("/add-username");
    return;
  }

  enqueueSnackbar("Login successful.", { variant: "success" });
  localStorage.setItem("isGuestLite", "false");
  // Full navigation so AppRouter mounts authenticated routes (guest `navigate` hits `*` → sign-in).
  window.location.assign(destination);
}
